import { useEffect, useState } from "react";
import { MdClose, MdVisibility } from "react-icons/md";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

Modal.setAppElement("#root");

export default function PartnerApprovalTable() {
  const [partners, setPartners] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await adminApiService.getAllPartners();

      if (!response.ok) throw new Error("Failed to fetch partners.");
      const data = await response.json();

      setPartners(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching partners.");
    }
  };

  const handleToggleApproval = async (partnerId, currentStatus) => {
    console.log(partnerId);

    const newStatus = currentStatus === "approved" ? "pending" : "approved";
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/partners/partner/${partnerId}/approval`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) throw new Error("Failed to update approval status.");
      toast.success("Partner status updated successfully!");
      fetchPartners();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error updating approval status.");
    }
  };

  const openModal = async (partnerId) => {
    try {
      const response = await fetch(
                  `${process.env.REACT_APP_API_URL}/admin/partners/partner/${partnerId}`
      );
      if (!response.ok) throw new Error("Failed to fetch partner details.");
      const data = await response.json();

      setSelectedPartner(data);
      setModalIsOpen(true);
    } catch (error) {
      console.error("Error fetching partner:", error);
      toast.error("Error fetching partner details.");
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedPartner(null);
  };

  const renderPdfLink = (label, fileUrl, path) =>
    fileUrl && (
      <div className="mt-4">
        <h3 className="mb-5 text-xl font-bold">{label}</h3>
        <Link
          className="mt-5 rounded-md bg-navy-900 p-2 text-white"
          onClick={() =>
            window.open(
              `${process.env.REACT_APP_API_URL_Plain}/config/uploads/${path}/${fileUrl}`
            )
          }
        >
          Open PDF
        </Link>
      </div>
    );

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <table className="w-full text-left text-sm text-gray-500">
        <thead className="bg-primary text-xs uppercase text-white">
          <tr>
            <th className="px-6 py-3">ID</th>
            <th className="px-6 py-3">Client Name</th>
            <th className="px-6 py-3">Partner Type</th>
            <th className="px-6 py-3">Core Business</th>
            <th className="px-6 py-3">Status</th>
            <th className="px-6 py-3">View</th>
          </tr>
        </thead>
        <tbody>
          {partners.map((partner, index) => (
            <tr key={index} className="border-b bg-white">
              <td className="px-6 py-4 text-base text-navy-900">
                {partner.partner_id}
              </td>
              <td className="px-6 py-4 text-base text-navy-900">
                {partner.client_name}
              </td>
              <td className="px-6 py-4 text-base text-navy-900">
                {partner.partner_type}
              </td>
              <td className="px-6 py-4 text-base text-navy-900">
                {partner.core_business}
              </td>

              <td className="px-6 py-4">
                <label className="inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={partner.user.status === "approved"}
                    onChange={() =>
                      handleToggleApproval(
                        partner.partner_id,
                        partner.user.status
                      )
                    }
                  />
                  <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-checked:bg-primary dark:peer-focus:ring-blue-800 rtl:peer-checked:after:-translate-x-full"></div>
                </label>
              </td>

              <td className="px-6 py-4">
                <button
                  aria-label="View Partner"
                  className="p-2 text-2xl text-primary hover:bg-gray-100"
                  onClick={() => openModal(partner.partner_id)}
                >
                  <MdVisibility />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Partner Details"
        className="text-black relative mx-auto max-w-2xl rounded-lg bg-white p-6 shadow-lg"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-navy-900 bg-opacity-50"
      >
        {selectedPartner && (
          <div>
            <button
              onClick={closeModal}
              className="absolute right-2 top-2 p-2 text-gray-700 hover:text-gray-900"
              aria-label="Close"
            >
              <MdClose className="text-2xl" />
            </button>

            <h2 className="mb-4 text-2xl font-bold">Partner Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p>
                  <strong>ID:</strong> {selectedPartner.partner_id}
                </p>
                <p>
                  <strong>Client Name:</strong> {selectedPartner.client_name}
                </p>
                <p>
                  <strong>Store Name:</strong> {selectedPartner.store_name}
                </p>
                <p>
                  <strong>Partner Type:</strong> {selectedPartner.partner_type}
                </p>
                <p>
                  <strong>Core Business:</strong>{" "}
                  {selectedPartner.core_business}
                </p>
              </div>
              <div>
                <p>
                  <strong>Delivery Address:</strong>{" "}
                  {selectedPartner.delivery_address}
                </p>
                <p>
                  <strong>District:</strong> {selectedPartner.district}
                </p>
                <p>
                  <strong>Division:</strong> {selectedPartner.division}
                </p>
              </div>
            </div>

            {renderPdfLink(
              "NID File",
              selectedPartner.nid_file_url,
              "nid_card_pdf"
            )}
            {renderPdfLink(
              "Trade License",
              selectedPartner.trade_license_url,
              "trade_license"
            )}
            {renderPdfLink(
              "TIN File",
              selectedPartner.tin_file_url,
              "tin_certificate"
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
